import { Task, TaskStatus } from '@/types';
import { User } from './permissions';
import { RaciChipInfo, TaskLinkCounts } from './types';
import { RACI_ROLE_CONFIG, RACI_ROLE_PRIORITY } from './constants';

/**
 * Get RACI role chip information for display
 * @param task - The task
 * @param user - Current user
 * @param currentRaciRole - Currently selected RACI tab (if in My Tasks view)
 * @returns Chip information or null if user has no role
 */
export const getUserRaciChip = (
  task: Task,
  user: User | null,
  currentRaciRole?: 'responsible' | 'accountable' | 'consulted' | 'informed'
): RaciChipInfo | null => {
  if (!user) return null;

  const userId = user.id;
  const roles: Array<'responsible' | 'accountable' | 'consulted' | 'informed'> = [];

  if (task.raci?.responsible?.includes(userId)) roles.push('responsible');
  if (task.raci?.accountable?.includes(userId)) roles.push('accountable');
  if (task.raci?.consulted?.includes(userId)) roles.push('consulted');
  if (task.raci?.informed?.includes(userId)) roles.push('informed');

  if (roles.length === 0) return null;

  // If currentRaciRole is specified and user has that role, show it
  const displayRole = currentRaciRole && roles.includes(currentRaciRole)
    ? currentRaciRole
    : roles.sort((a, b) => RACI_ROLE_PRIORITY[b] - RACI_ROLE_PRIORITY[a])[0];

  const config = RACI_ROLE_CONFIG[displayRole];
  const allRoleLabels = roles.map(r => RACI_ROLE_CONFIG[r].label);

  return {
    label: config.label,
    color: config.color,
    bgColor: config.bgColor,
    allRoles: allRoleLabels,
    hasMultipleRoles: roles.length > 1,
  };
};

/**
 * Get task link/dependency counts
 */
export const getTaskLinkCounts = (task: Task): TaskLinkCounts => {
  const subtasks = task.subtaskIds?.length || 0;
  const blockedBy = task.blockedByTaskIds?.length || 0;
  const blocking = task.blockingTaskIds?.length || 0;

  return {
    subtasks,
    blockedBy,
    blocking,
    hasAnyLinks: subtasks > 0 || blockedBy > 0 || blocking > 0,
  };
};

/**
 * Create a lookup map for tasks by ID (O(1) access)
 */
export const createTaskLookupMap = (tasks: Task[]): Map<string, Task> => {
  return new Map(tasks.map(task => [task.id, task]));
};

/**
 * Check if task has blocking dependencies (incomplete tasks it depends on)
 * @param task - The task to check
 * @param allTasks - All tasks to check against
 * @returns true if task has incomplete blocking dependencies
 */
export const hasBlockingDependencies = (task: Task, allTasks: Task[]): boolean => {
  if (!task.blockedByTaskIds || task.blockedByTaskIds.length === 0) {
    return false;
  }

  const taskMap = createTaskLookupMap(allTasks);

  return task.blockedByTaskIds.some(blockerId => {
    const blocker = taskMap.get(blockerId);
    return blocker && blocker.status !== TaskStatus.COMPLETED;
  });
};

/**
 * Check if task has incomplete subtasks
 * @param task - The task to check
 * @param allTasks - All tasks to check against
 * @returns true if task has incomplete subtasks
 */
export const hasIncompleteSubtasks = (task: Task, allTasks: Task[]): boolean => {
  if (!task.subtaskIds || task.subtaskIds.length === 0) {
    return false;
  }

  const taskMap = createTaskLookupMap(allTasks);

  return task.subtaskIds.some(subtaskId => {
    const subtask = taskMap.get(subtaskId);
    return subtask && subtask.status !== TaskStatus.COMPLETED;
  });
};

/**
 * Get blocking task details for warning message
 * @param task - The task to check
 * @param allTasks - All tasks to check against
 * @returns Array of incomplete blocking tasks
 */
export const getBlockingTasks = (task: Task, allTasks: Task[]): Task[] => {
  if (!task.blockedByTaskIds || task.blockedByTaskIds.length === 0) {
    return [];
  }

  const taskMap = createTaskLookupMap(allTasks);

  return task.blockedByTaskIds
    .map(blockerId => taskMap.get(blockerId))
    .filter((blocker): blocker is Task => 
      blocker !== undefined && blocker.status !== TaskStatus.COMPLETED
    );
};

/**
 * Get incomplete subtasks
 * @param task - The task to check
 * @param allTasks - All tasks to check against
 * @returns Array of incomplete subtasks
 */
export const getIncompleteSubtasks = (task: Task, allTasks: Task[]): Task[] => {
  if (!task.subtaskIds || task.subtaskIds.length === 0) {
    return [];
  }

  const taskMap = createTaskLookupMap(allTasks);

  return task.subtaskIds
    .map(subtaskId => taskMap.get(subtaskId))
    .filter((subtask): subtask is Task => 
      subtask !== undefined && subtask.status !== TaskStatus.COMPLETED
    );
};

/**
 * Validate if task can be completed (no blockers or incomplete subtasks)
 * @param task - The task to validate
 * @param allTasks - All tasks to check against
 * @returns Object with validation result and reason
 */
export const validateTaskCompletion = (
  task: Task,
  allTasks: Task[]
): { canComplete: boolean; reason?: string; blockingTasks?: Task[] } => {
  // Check for blocking dependencies
  const blockingTasks = getBlockingTasks(task, allTasks);
  if (blockingTasks.length > 0) {
    return {
      canComplete: false,
      reason: `This task is blocked by ${blockingTasks.length} incomplete task${blockingTasks.length > 1 ? 's' : ''}`,
      blockingTasks,
    };
  }

  // Check for incomplete subtasks
  const incompleteSubtasks = getIncompleteSubtasks(task, allTasks);
  if (incompleteSubtasks.length > 0) {
    return {
      canComplete: false,
      reason: `This task has ${incompleteSubtasks.length} incomplete subtask${incompleteSubtasks.length > 1 ? 's' : ''}`,
      blockingTasks: incompleteSubtasks,
    };
  }

  return { canComplete: true };
};

/**
 * Check if task is overdue
 */
export const isTaskOverdue = (task: Task): boolean => {
  if (task.status === TaskStatus.COMPLETED) return false;
  return new Date(task.dueDate) < new Date();
};

/**
 * Get parent task from task map
 */
export const getParentTask = (task: Task, taskMap: Map<string, Task>): Task | undefined => {
  if (!task.parentTaskId) return undefined;
  return taskMap.get(task.parentTaskId);
};
